import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
// Audience-projected wire contract: Server 993c83b, shipment-history presenter/query.
// Recorded values are display evidence only; current APIs own operational decisions.
export type HistoryValue =
  | string
  | number
  | boolean
  | null
  | HistoryValue[]
  | { [key: string]: HistoryValue };
export interface HistoryDefinition {
  availability:
    | "AVAILABLE"
    | "REDACTED"
    | "UNSUPPORTED_SCHEMA"
    | "INVALID_RECORDED_DEFINITION";
  value: { [key: string]: HistoryValue } | null;
}
export interface ShipmentHistoryRevision {
  id: string;
  shipmentId: string;
  sequence: number;
  previousRevisionId: string | null;
  predecessorAvailability: string;
  schemaVersion: number;
  kind: "CREATED" | "APPLIED_CHANGE" | "OBSERVED_BASELINE";
  recordedAt: string;
  actor: { role: string };
  cause: {
    requestId: string | null;
    reviewId: string | null;
    operationId?: string | null;
    authorizingPolicy?: string;
  };
  definition: HistoryDefinition;
  diff: {
    availability: string;
    fields:
      | { path: string; before: HistoryValue; after: HistoryValue }[]
      | null;
  };
}
export interface ShipmentHistoryPage {
  earlierCorrections?: {
    items: { id: string; kind: "APPLIED_CORRECTION"; appliedAt: string }[];
    hasMore: boolean;
  };
  items: ShipmentHistoryRevision[];
  page: { snapshotSequence: number; nextCursor: string | null };
  current: {
    latestVisibleRevision: { id: string; sequence: number } | null;
    writeVersion: number;
    enrolled: boolean;
    updatedAt: string;
  };
}

export function historyErrorLabel(error: unknown) {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status === 401 || status === 403 || status === 404
    ? "denied"
    : status === 503
      ? "unavailable"
      : "error";
}
async function fetchHistory(
  token: string,
  shipmentId: string,
  signal: AbortSignal,
  cursor?: string,
  revisionId?: string
) {
  const { data } = await axios.get<{ data: ShipmentHistoryPage }>(
    `${import.meta.env.VITE_API_URL}/api/v2/shipments/${encodeURIComponent(shipmentId)}/revisions${revisionId ? `/${encodeURIComponent(revisionId)}` : ""}`,
    {
      params: cursor ? { cursor } : undefined,
      headers: { Authorization: `Bearer ${token}` },
      signal,
    }
  );
  return data.data;
}
export function useShipmentHistory(
  shipmentId: string,
  enabled: boolean,
  updatedAt?: string
) {
  const { token, customer } = useSession();
  return useInfiniteQuery({
    queryKey: ["shipmentHistory", customer?.id, shipmentId, "pages", updatedAt],
    initialPageParam: emptyCursor(),
    queryFn: ({ pageParam, signal }) =>
      fetchHistory(token ?? "", shipmentId, signal, pageParam),
    getNextPageParam: (page) => page.page.nextCursor ?? undefined,
    enabled: !!token && !!customer?.id && enabled,
    // Only mounted while history is open; refresh every loaded authorized page.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}
export function useShipmentHistoryRevision(
  shipmentId: string,
  revisionId: string | null,
  enabled: boolean
) {
  const { token, customer } = useSession();
  return useQuery({
    queryKey: [
      "shipmentHistory",
      customer?.id,
      shipmentId,
      "revision",
      revisionId,
    ],
    queryFn: ({ signal }) =>
      fetchHistory(
        token ?? "",
        shipmentId,
        signal,
        undefined,
        revisionId ?? undefined
      ),
    enabled: !!token && !!customer?.id && !!revisionId && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

function emptyCursor(): string | undefined {
  return undefined;
}
